import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, isToday, isTomorrow, isPast, addDays, startOfWeek, isSameDay } from 'date-fns';
import { 
  Calendar, Clock, MapPin, ChevronLeft, ChevronRight, 
  Loader2, Bell, Coffee, Sun
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import MobileLayout from '../components/layout/MobileLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-green-100 text-green-700 border-green-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  missed: 'bg-red-100 text-red-700 border-red-200',
};

const ShiftCard = ({ shift }) => {
  const shiftDate = parseISO(shift.date);
  const isShiftToday = isToday(shiftDate);
  const isShiftTomorrow = isTomorrow(shiftDate);
  const isShiftPast = isPast(shiftDate) && !isShiftToday;
  
  return (
    <Card 
      className={`transition-all ${
        isShiftToday 
          ? 'border-blue-400 bg-blue-50/50 shadow-md' 
          : isShiftPast 
            ? 'opacity-60' 
            : 'border-slate-200'
      }`}
      data-testid={`shift-card-${shift.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {isShiftToday && (
                <Badge className="bg-blue-600 text-white text-[10px]">TODAY</Badge>
              )}
              {isShiftTomorrow && (
                <Badge variant="outline" className="border-amber-400 text-amber-600 text-[10px]">TOMORROW</Badge>
              )}
            </div>
            <p className="font-semibold text-slate-800">
              {format(shiftDate, 'EEEE, MMMM d')}
            </p>
          </div>
          <Badge 
            variant="outline" 
            className={`text-xs ${STATUS_STYLES[shift.status] || STATUS_STYLES.scheduled}`}
          >
            {shift.status}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-slate-600">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="font-medium">
              {shift.start_time} - {shift.end_time}
            </span>
          </div>
          
          {shift.site_name && (
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4 text-green-600" />
              <span>{shift.site_name}</span>
            </div>
          )}
          
          {shift.notes && (
            <div className="mt-2 p-2 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">{shift.notes}</p>
            </div>
          )}
        </div>
        
        {isShiftToday && shift.status === 'scheduled' && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="flex items-center gap-2 text-blue-700">
              <Bell className="w-4 h-4" />
              <span className="text-sm font-medium">Remember to clock in!</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Full Day Card for Week View - More Readable
const WeekDayCard = ({ date, shifts, isCurrentDay }) => {
  const dayName = format(date, 'EEEE');
  const dayNum = format(date, 'd');
  const monthName = format(date, 'MMM');
  const hasShifts = shifts.length > 0;
  const isPastDay = isPast(date) && !isToday(date);
  
  return (
    <Card 
      className={`transition-all ${
        isCurrentDay 
          ? 'border-blue-400 shadow-lg ring-2 ring-blue-200' 
          : hasShifts 
            ? 'border-green-200' 
            : isPastDay
              ? 'border-slate-100 opacity-50'
              : 'border-slate-200'
      }`}
      data-testid={`week-day-${format(date, 'yyyy-MM-dd')}`}
    >
      {/* Day Header */}
      <div className={`px-4 py-3 border-b ${
        isCurrentDay 
          ? 'bg-blue-600 text-white' 
          : hasShifts 
            ? 'bg-green-50 text-green-800'
            : 'bg-slate-50 text-slate-600'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-semibold ${isCurrentDay ? 'text-blue-100' : ''}`}>{dayName}</p>
            <p className={`text-xl font-bold ${isCurrentDay ? 'text-white' : 'text-slate-800'}`}>
              {monthName} {dayNum}
            </p>
          </div>
          {isCurrentDay && (
            <Badge className="bg-white text-blue-600 text-xs">TODAY</Badge>
          )}
          {hasShifts && !isCurrentDay && (
            <Badge variant="outline" className="border-green-400 text-green-700 text-xs">
              {shifts.length} shift{shifts.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Day Content */}
      <CardContent className="p-3">
        {hasShifts ? (
          <div className="space-y-3">
            {shifts.map((shift, index) => (
              <div 
                key={shift.id}
                className={`p-3 rounded-lg ${
                  isCurrentDay 
                    ? 'bg-blue-50 border border-blue-200' 
                    : 'bg-slate-50 border border-slate-100'
                }`}
              >
                {/* Time */}
                <div className="flex items-center gap-2 mb-2">
                  <div className={`p-1.5 rounded-md ${isCurrentDay ? 'bg-blue-100' : 'bg-white'}`}>
                    <Clock className={`w-4 h-4 ${isCurrentDay ? 'text-blue-600' : 'text-slate-600'}`} />
                  </div>
                  <span className="font-bold text-slate-800 text-base">
                    {shift.start_time} - {shift.end_time}
                  </span>
                </div>
                
                {/* Site */}
                {shift.site_name && (
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-md ${isCurrentDay ? 'bg-green-100' : 'bg-white'}`}>
                      <MapPin className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-slate-700 text-sm font-medium">
                      {shift.site_name}
                    </span>
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="mt-2 flex items-center justify-between">
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${STATUS_STYLES[shift.status] || STATUS_STYLES.scheduled}`}
                  >
                    {shift.status}
                  </Badge>
                  {isCurrentDay && shift.status === 'scheduled' && (
                    <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Clock in!
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-6 flex flex-col items-center justify-center text-center">
            {isPastDay ? (
              <>
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <Coffee className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-400 font-medium">No Shift</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-2">
                  <Sun className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-sm text-amber-600 font-semibold">Day Off</p>
                <p className="text-xs text-slate-400 mt-1">Enjoy your rest!</p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Schedule() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [view, setView] = useState('upcoming'); // 'upcoming' or 'week'

  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getMyShifts();
      setShifts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading shifts:', error);
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const navigateWeek = (direction) => {
    setCurrentWeekStart(prev => addDays(prev, direction * 7));
  };

  // Generate week days array
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Get shifts for a specific date
  const getShiftsForDate = (date) => {
    return shifts.filter(shift => {
      if (!shift.date) return false;
      return isSameDay(parseISO(shift.date), date);
    });
  };

  // Filter shifts for upcoming view
  const upcomingShifts = shifts.filter(shift => {
    if (!shift.date) return false;
    const shiftDate = parseISO(shift.date);
    return !isPast(shiftDate) || isToday(shiftDate);
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const todayShifts = shifts.filter(s => s.date && isToday(parseISO(s.date)));
  const upcomingCount = upcomingShifts.length;

  // Count shifts this week
  const weekShiftCount = weekDays.reduce((count, day) => {
    return count + getShiftsForDate(day).length;
  }, 0);

  // Count days off this week
  const daysOffCount = weekDays.filter(day => getShiftsForDate(day).length === 0).length;

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="p-4 space-y-4" data-testid="schedule-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">My Schedule</h1>
            <p className="text-slate-500 text-sm">
              {upcomingCount} upcoming shift{upcomingCount !== 1 ? 's' : ''}
            </p>
          </div>
          <Calendar className="w-8 h-8 text-blue-600" />
        </div>

        {/* Today's Shift Alert */}
        {todayShifts.length > 0 && view === 'upcoming' && (
          <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold">Today's Shift</p>
                  <p className="text-blue-100">
                    {todayShifts[0].start_time} - {todayShifts[0].end_time}
                    {todayShifts[0].site_name && ` at ${todayShifts[0].site_name}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Toggle */}
        <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
          <Button
            variant={view === 'upcoming' ? 'default' : 'ghost'}
            size="sm"
            className={`flex-1 ${view === 'upcoming' ? '' : 'text-slate-600'}`}
            onClick={() => setView('upcoming')}
          >
            Upcoming
          </Button>
          <Button
            variant={view === 'week' ? 'default' : 'ghost'}
            size="sm"
            className={`flex-1 ${view === 'week' ? '' : 'text-slate-600'}`}
            onClick={() => setView('week')}
          >
            Week View
          </Button>
        </div>

        {/* Week View */}
        {view === 'week' && (
          <>
            {/* Week Navigation */}
            <Card className="bg-white">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Prev
                  </Button>
                  <div className="text-center">
                    <p className="font-bold text-slate-800">
                      {format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}
                    </p>
                    <div className="flex gap-4 justify-center mt-1">
                      <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {weekShiftCount} shifts
                      </span>
                      <span className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                        <Sun className="w-3 h-3" /> {daysOffCount} days off
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Calendar Week - Vertical List for Better Readability */}
            <div className="space-y-3">
              {weekDays.map((day) => (
                <WeekDayCard 
                  key={day.toISOString()} 
                  date={day} 
                  shifts={getShiftsForDate(day)}
                  isCurrentDay={isToday(day)}
                />
              ))}
            </div>
          </>
        )}

        {/* Upcoming Shifts List */}
        {view === 'upcoming' && (
          <div className="space-y-3">
            {upcomingShifts.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No upcoming shifts</p>
                  <p className="text-slate-400 text-sm mt-1">
                    You have no scheduled shifts coming up
                  </p>
                </CardContent>
              </Card>
            ) : (
              upcomingShifts.map((shift) => (
                <ShiftCard key={shift.id} shift={shift} />
              ))
            )}
          </div>
        )}

        {/* Stats */}
        {shifts.length > 0 && view === 'upcoming' && (
          <Card className="mt-4">
            <CardContent className="p-4">
              <p className="text-xs text-slate-400 uppercase font-medium mb-2">This Month</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-800">{shifts.length}</p>
                  <p className="text-xs text-slate-500">Total Shifts</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {shifts.filter(s => s.status === 'completed').length}
                  </p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{upcomingCount}</p>
                  <p className="text-xs text-slate-500">Upcoming</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MobileLayout>
  );
}
